/**
 * @deprecated Import types from `./SessionTypes` instead.
 *
 * This file is kept for backwards compatibility. All type definitions
 * have been extracted to {@link ./SessionTypes}. The `SessionState` class
 * itself is replaced by {@link SessionCommands} (command dispatch) and
 * {@link SessionStore} (read-only state projection).
 */

import {Subject, Subscription} from 'rxjs';
import {SessionResultFailure} from './AsyncSessionResult';
import {CpuPressureLevel} from './PerformanceMonitor';

// ── Re-export ALL types from SessionTypes for backwards compatibility ───────
export {
  SessionParticipant,
  ViewMode,
  ZoomOptions,
  SessionCapabilities,
  TimerPayload,
  UserIdType,
  MediaPlaybackFile,
  MediaDevice,
  DeviceStarted,
  AudioStarted,
  VideoDimension,
  VideoCanvasDimension,
  StopRenderParticipant,
  MediaPlayback,
  MusicPlayback,
  RenderParticipant,
  SupportedVideoElement,
  SupportedVideoElementType,
  AttachVideoRequest,
  DetachVideoRequest,
  AttachedVideoUpdate,
  MediaState,
  SessionInitialization,
  InstructionVideo,
  ConnectionStatus,
  ConnectionChange,
  NetworkQualityPayload,
} from './SessionTypes';

// Re-import types needed by the legacy class body below
import type {
  MediaDevice,
  MediaPlayback,
  AttachVideoRequest,
  DetachVideoRequest,
  AttachedVideoUpdate,
  RenderParticipant,
  StopRenderParticipant,
  VideoCanvasDimension,
  MediaState,
  DeviceStarted,
  AudioStarted,
  MusicPlayback,
  InstructionVideo,
  ConnectionChange,
  NetworkQualityPayload,
  SessionInitialization,
  UserIdType,
} from './SessionTypes';
import type {SessionParticipant} from './SessionParticipant';
import type {SessionChatMessage} from './SessionChatMessage';
import type {SessionCommand} from './SessionCommand';
import type {ViewMode} from './ViewMode';
import type {ZoomOptions} from './ZoomOptions';
import type {SessionCapabilities} from './SessionCapabilities';
import type {TimerPayload} from './TimerPayload';

// ── Legacy state-variable helpers (kept for SessionState class body) ────────

export type UpdatableStateVariable<S> = {
  value: S,
  updateTx: Subject<S>
  update: (s: S) => void
}

export type RequestStateVariable<S> = {
  value: S,
  requestTx?: Subject<S>
  request: (s: S) => void
}

export type BiDirectionalStateVariable<S> = {
  value: S,
  updateTx: Subject<S>
  update: (s: S) => void
  requestTx?: Subject<S>
  request: (s: S) => void
  requestAndWaitForUpdate: (s: S, onTimeout?: () => void, timeout?: number) => Promise<S|SessionResultFailure>
}

function useRequestOnlyState<S>(initialState: S, subscriptions?: Subscription[]): RequestStateVariable<S> {
  const requestTx = new Subject<S>();
  const setAndGet = {
    value: initialState,
    requestTx,
    request: (s: S) => requestTx.next(s),
  }
  const sub = setAndGet.requestTx.subscribe((val) => setAndGet.value = val);
  if (subscriptions) subscriptions.push(sub);
  return setAndGet
}

function useUpdatableOnlyState<S>(initialState: S, subscriptions?: Subscription[]): UpdatableStateVariable<S> {
  const updateTx = new Subject<S>();
  const setAndGet = {
    value: initialState,
    updateTx,
    update: (s: S) => updateTx.next(s),
  }
  const sub = setAndGet.updateTx.subscribe((val) => setAndGet.value = val);
  if (subscriptions) subscriptions.push(sub);
  return setAndGet
}

function useBidirectionalState<S>(initialState: S, subscriptions?: Subscription[]): BiDirectionalStateVariable<S> {
  const requestTx = new Subject<S>();
  const updateTx = new Subject<S>();
  const setAndGet = {
    value: initialState,
    requestTx,
    updateTx,
    update: (s: S) => updateTx.next(s),
    request: (s: S) => requestTx.next(s),
  } as BiDirectionalStateVariable<S>
  setAndGet.requestAndWaitForUpdate = (s: S, onTimeout?: () => void, timeout?: number) => {
    return new Promise<S | SessionResultFailure>((resolve) => {
      let settled = false;
      const subscription = setAndGet.updateTx.subscribe((update) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        subscription.unsubscribe();
        resolve(update);
      });
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        subscription.unsubscribe();
        if (onTimeout) {
          onTimeout();
        }
        resolve({ errorCode: 'TIMEOUT', errorMessage: 'requestAndWaitForUpdate timed out' } as unknown as SessionResultFailure);
      }, timeout ?? 30000);
      setAndGet.request(s);
    });
  }
  const sub = setAndGet.updateTx.subscribe((val) => setAndGet.value = val);
  if (subscriptions) subscriptions.push(sub);
  return setAndGet
}

// ── Deprecated class ────────────────────────────────────────────────────────

/**
 * @deprecated Use {@link SessionCommands} for dispatching commands and
 * {@link SessionStore} for reading reactive state. This class is retained
 * only so that existing provider internals continue to compile during
 * the transition period. No new code should depend on it.
 */
export class SessionState {
  private _subscriptions: Subscription[] = [];

  joined = useUpdatableOnlyState<SessionInitialization|undefined>(undefined, this._subscriptions)
  requestCamerasRefresh = useRequestOnlyState<void>(undefined, this._subscriptions)
  requestSpeakersRefresh = useRequestOnlyState<void>(undefined, this._subscriptions)
  requestMicrophonesRefresh = useRequestOnlyState<void>(undefined, this._subscriptions)
  cameras = useUpdatableOnlyState<MediaDevice[]>([], this._subscriptions)
  speakers = useUpdatableOnlyState<MediaDevice[]>([], this._subscriptions)
  microphones = useUpdatableOnlyState<MediaDevice[]>([], this._subscriptions)
  audioVolume = useUpdatableOnlyState<{userId: UserIdType, volume: number}|undefined>(undefined, this._subscriptions)
  startMediaPlayBack = useRequestOnlyState<MediaPlayback|undefined>(undefined, this._subscriptions)
  currentCamera = useBidirectionalState<MediaDevice|undefined>(undefined, this._subscriptions)
  currentSpeaker = useBidirectionalState<MediaDevice|undefined>(undefined, this._subscriptions)
  currentMicroPhone = useBidirectionalState<MediaDevice|undefined>(undefined, this._subscriptions)
  defaultCamera = useBidirectionalState<MediaDevice|undefined>(undefined, this._subscriptions)
  defaultSpeaker = useBidirectionalState<MediaDevice|undefined>(undefined, this._subscriptions)
  defaultMicrophone = useBidirectionalState<MediaDevice|undefined>(undefined, this._subscriptions)

  renderParticipant = useRequestOnlyState<RenderParticipant|undefined>(undefined, this._subscriptions)
  attachVideo = useRequestOnlyState<AttachVideoRequest|undefined>(undefined, this._subscriptions)
  detachVideo = useRequestOnlyState<DetachVideoRequest|undefined>(undefined, this._subscriptions)
  attachedVideo = useUpdatableOnlyState<AttachedVideoUpdate|undefined>(undefined, this._subscriptions)
  adjustRenderedParticipant = useRequestOnlyState<RenderParticipant|undefined>(undefined, this._subscriptions)
  adjustQuality = useRequestOnlyState<RenderParticipant|undefined>(undefined, this._subscriptions)
  stopRenderParticipant = useRequestOnlyState<StopRenderParticipant|undefined>(undefined, this._subscriptions)
  videoCanvasDimension = useBidirectionalState<VideoCanvasDimension|undefined>(undefined, this._subscriptions)

  mediaState = useBidirectionalState<MediaState|undefined>(undefined, this._subscriptions)
  restartVideo = useRequestOnlyState<void>(undefined, this._subscriptions)
  restartAudio = useRequestOnlyState<void>(undefined, this._subscriptions)
  videoStarted = useBidirectionalState<DeviceStarted|undefined>(undefined, this._subscriptions)
  audioStarted = useBidirectionalState<AudioStarted|undefined>(undefined, this._subscriptions)
  activeSpeaker = useUpdatableOnlyState<UserIdType[]>([], this._subscriptions)
  requestUnmute = useBidirectionalState<boolean>(false, this._subscriptions)
  chat = useBidirectionalState<SessionChatMessage|undefined>(undefined, this._subscriptions)
  commandChannelConnected = useUpdatableOnlyState<boolean>(false, this._subscriptions)
  command = useBidirectionalState<SessionCommand|undefined>(undefined, this._subscriptions)
  peerVideoStarted = useUpdatableOnlyState<{ userId: number, started: boolean} | undefined>(undefined, this._subscriptions)
  audio = useBidirectionalState<string|undefined>(undefined, this._subscriptions)
  isMirrored = useBidirectionalState<boolean|undefined>(undefined, this._subscriptions)
  isBlur = useBidirectionalState<boolean|undefined>(undefined, this._subscriptions)
  isMuted = useBidirectionalState<{ muted: boolean; userId?: number} | undefined>(undefined, this._subscriptions)
  isMuteAll = useBidirectionalState<boolean|undefined>(undefined, this._subscriptions)
  mutedByHost = useUpdatableOnlyState<boolean|undefined>(undefined, this._subscriptions)
  unmutedByHost = useUpdatableOnlyState<boolean|undefined>(undefined, this._subscriptions)
  participants = useUpdatableOnlyState<SessionParticipant[]>([], this._subscriptions)
  participantsRendered = useUpdatableOnlyState<SessionParticipant[]>([], this._subscriptions)
  sessionEnded = useBidirectionalState<boolean>(false, this._subscriptions)
  currentUserId = useBidirectionalState<number|undefined>(undefined, this._subscriptions)
  error = useUpdatableOnlyState<string|undefined>(undefined, this._subscriptions)
  view = useBidirectionalState<ViewMode|undefined>(undefined, this._subscriptions)
  toggleZoom = useUpdatableOnlyState<ZoomOptions>({ enabled: false }, this._subscriptions)
  instructionVideo = useUpdatableOnlyState<InstructionVideo|undefined>(undefined, this._subscriptions)
  connectionChange = useUpdatableOnlyState<ConnectionChange|undefined>(undefined, this._subscriptions)
  networkQualityChange = useUpdatableOnlyState<NetworkQualityPayload|undefined>(undefined, this._subscriptions)
  sessionCapabilities = useUpdatableOnlyState<SessionCapabilities|undefined>(undefined, this._subscriptions)
  timerStarted = useUpdatableOnlyState<TimerPayload|undefined>(undefined, this._subscriptions)
  reconnect = useRequestOnlyState<void>(undefined, this._subscriptions)
  musicStarted = useBidirectionalState<MusicPlayback|undefined>(undefined, this._subscriptions)
  musicNext = useRequestOnlyState<void>(undefined, this._subscriptions)
  musicVolume = useUpdatableOnlyState<number>(0.5, this._subscriptions)
  cpuPressure = useUpdatableOnlyState<CpuPressureLevel>('normal', this._subscriptions)

  destroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }
}

/**
 * @deprecated Unused. Will be removed in a future cleanup.
 */
export class ClientSessionState {

}
